import type { EtlJobCatalog } from '../types/etlJobs'

/**
 * Whether the server treats this caller as a Lighthouse.Admin holder.
 *
 * - `admin` / `denied`: the server's answer, from the catalogue's `permission`.
 * - `pending`: NOT ANSWERED YET — the catalogue has not arrived and has not
 *   failed. An admin surface waits (a skeleton) and fetches nothing: fetching
 *   then races the answer, and on a cold load (a reload, a bookmark, a shared
 *   deep link) a denied caller's requests go out before the "no" arrives.
 * - `unknown`: ANSWERED, BUT CANNOT SAY — the catalogue failed to load, or came
 *   from an API that predates the permission field. An admin surface renders
 *   and routes normally and handles its own 403, so a missing answer never
 *   locks an administrator out.
 */
export type AdminGate =
  | { state: 'admin' }
  | { state: 'denied'; reason: string }
  | { state: 'pending' }
  | { state: 'unknown' }

/**
 * Derives the admin gate — for the administrator READ pages — from the job
 * catalogue's `permission`.
 *
 * PREMISE (verified): `permission.isLighthouseAdmin` IS the LighthouseAdmin policy
 * as the server evaluated it for this caller's MultiAuth principal —
 * EtlJobCatalogController calls IAuthorizationService.AuthorizeAsync with
 * LighthouseAuthorizationPolicies.Admin — and the governance, registry,
 * retirement, schedule-load, configuration and audit controllers carry that same
 * policy at class level, on MultiAuth. So one answer gates them all.
 *
 * NOT `canTriggerJobs`: that is now the answer for the two ETL WRITES, which are
 * also pinned to an Entra sign-in, so the shared API key in Development is an
 * administrator here and refused there. Reading it would lock that session out
 * of every read page. An older API serves no `isLighthouseAdmin`; there
 * `canTriggerJobs` still carried the read meaning, so it is the fallback. Write
 * affordances read {@link deriveRunGate} (running a job) and
 * {@link deriveWriteGate} (clearing a halt, handling a signal, cancelling a run,
 * intervening on the request backstop) instead.
 *
 * A catalogue in hand is used WHATEVER `isError` says: a background refetch that
 * fails (a window-focus refetch, say) keeps the last answer in `data`, and that
 * answer is still the last thing the server said about this caller. Dropping it
 * to `unknown` would un-dim a denied caller's rail and mount every admin page's
 * fetching body, while the Scheduler and the Explorer's Rerun — which keep the
 * cached permission — still said the caller could not run. `isError` decides
 * only when there is no catalogue at all: failed is `unknown`, not yet is `pending`.
 *
 * Never read token roles instead: the policy degrades to "any authenticated user"
 * in Development, and an API-key session carries no roles at all, so a client
 * re-deriving the rule is wrong in one environment or the other (types/etlJobs.ts
 * explains this at EtlTriggerPermission).
 */
export function deriveAdminGate(catalog: EtlJobCatalog | undefined, isError: boolean): AdminGate {
  if (catalog) {
    const permission = catalog.permission
    if (typeof permission?.isLighthouseAdmin === 'boolean') {
      return permission.isLighthouseAdmin
        ? { state: 'admin' }
        : { state: 'denied', reason: permission.adminDeniedReason ?? permission.deniedReason ?? '' }
    }
    return runGateFrom(permission)
  }
  return isError ? { state: 'unknown' } : { state: 'pending' }
}

/**
 * Whether this caller may RUN a job — the Bearer-pinned write — from the same
 * catalogue: `canTriggerJobs`, with its `deniedReason`. Same states and the same
 * `pending`/`unknown` rules as {@link deriveAdminGate}.
 *
 * For surfaces that lead to a run request (the Data Comb's "Run a pass now", the
 * Execution History's "Open in Scheduler"): they must say what the Scheduler will
 * say, and in Development the shared API key is an administrator for the read
 * pages yet cannot run anything.
 */
export function deriveRunGate(catalog: EtlJobCatalog | undefined, isError: boolean): AdminGate {
  if (catalog) return runGateFrom(catalog.permission)
  return isError ? { state: 'unknown' } : { state: 'pending' }
}

/**
 * The write flags {@link deriveWriteGate} reads. `canTriggerJobs` is {@link deriveRunGate}'s. `canHandleRequests` is
 * the request backstop's interventions (Cartographer doc 15 §7) — Observatory's one act surface over Cartographer data.
 */
export type WriteFlag = 'canClearHalts' | 'canHandleSignals' | 'canCancelRuns' | 'canHandleRequests'

/** What a write gate says when the catalogue carries no answer for the flag at all. */
export const WRITE_NOT_OFFERED = 'This API does not offer this action.'

/**
 * Whether this caller may perform one of Observatory's other Bearer-pinned
 * WRITES — clear a halt, handle a signal, cancel a run, intervene on the request
 * backstop — from the same catalogue: the named `flag`, with the write
 * `deniedReason`.
 *
 * - `true` is `admin`; `false` is `denied`, with the server's reason.
 * - ABSENT (or null) is `denied` with {@link WRITE_NOT_OFFERED}. Not `unknown`:
 *   an older API that does not publish the flag does not offer the action, so
 *   there is nothing to try — a write fails closed, where an admin READ page
 *   on `unknown` still renders and handles its own 403.
 * - With no catalogue in hand: `pending` while unanswered, `unknown` when it
 *   failed — exactly as {@link deriveRunGate}.
 *
 * WRITE AFFORDANCES RENDER ONLY ON STATE `admin`. `denied` shows the reason
 * (or nothing); `pending` and `unknown` show no control at all.
 */
export function deriveWriteGate(catalog: EtlJobCatalog | undefined, isError: boolean, flag: WriteFlag): AdminGate {
  if (catalog) {
    const permission = catalog.permission
    const answer = permission?.[flag]
    if (answer === true) return { state: 'admin' }
    if (answer === false) return { state: 'denied', reason: permission?.deniedReason ?? '' }
    return { state: 'denied', reason: WRITE_NOT_OFFERED }
  }
  return isError ? { state: 'unknown' } : { state: 'pending' }
}

function runGateFrom(permission: EtlJobCatalog['permission']): AdminGate {
  if (permission?.canTriggerJobs === true) return { state: 'admin' }
  if (permission?.canTriggerJobs === false) return { state: 'denied', reason: permission.deniedReason ?? '' }
  return { state: 'unknown' }
}

/**
 * Whether an admin PAGE may fetch its endpoint: once the gate has answered, and
 * not with a refusal. `unknown` fetches (never lock an administrator out);
 * `pending` does not (the answer is on its way); `denied` never does. Previews
 * and badges are stricter still — they fetch only on `admin`.
 */
export function adminPageMayFetch(gate: AdminGate): boolean {
  return gate.state === 'admin' || gate.state === 'unknown'
}
